// src/features/admin/roleMasterApi.ts - UPDATED: role_id for update/delete
import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { adminRefreshToken } from '../../features/admin/adminApi.ts';

export interface Role {
  id: number;
  role_name: string;
  role_desc: string | null;
  createddt: string;
  updateddt: string | null;
}

export interface AddRoleRequest {
  role_name: string;
  role_desc: string;
}

export interface AddRoleResponse {
  error: boolean;
  message: string;
  recId?: number;
}

export interface ListRolesResponse {
  error: boolean;
  message: string;
  roles?: Role[];
}

// ✅ Backend expects role_id (not id)
export interface UpdateRoleRequest {
  role_id: number;
  role_name: string;
  role_desc: string;
}

export interface UpdateRoleResponse {
  error: boolean;
  message: string;
}

export interface DeleteRoleResponse {
  error: boolean;
  message: string;
}

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT;
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

const baseQueryWithRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args, api, extraOptions
) => {
  let token = localStorage.getItem('admin_token');
  if (!token) token = await adminRefreshToken();

  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  headers.set('authkey', AUTH_KEY);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const fetchArgs: FetchArgs = typeof args === 'string' ? { url: args, headers } : { ...args, headers };
  const result = await fetchBaseQuery({ baseUrl: BASE_URL, prepareHeaders: () => headers })(fetchArgs, api, extraOptions);

  // Retry on 401
  if (result.error && result.error.status === 401) {
    const newToken = await adminRefreshToken();
    if (newToken) {
      headers.set('Authorization', `Bearer ${newToken}`);
      const retryArgs: FetchArgs = typeof args === 'string' ? { url: args, headers } : { ...args, headers };
      return await fetchBaseQuery({ baseUrl: BASE_URL, prepareHeaders: () => headers })(retryArgs, api, extraOptions);
    }
  }

  return result;
};

export const roleMasterApi = createApi({
  reducerPath: 'roleMasterApi',
  baseQuery: baseQueryWithRefresh,
  tagTypes: ['Roles'],
  endpoints: (build) => ({

    addRole: build.mutation<AddRoleResponse, AddRoleRequest>({
      query: (data) => ({
        url: '/admin/add_role',
        method: 'POST',
        body: JSON.stringify({
          role_name: data.role_name.trim(),
          role_desc: data.role_desc.trim(),
        }),
      }),
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || 'Role added successfully',
        recId: response.recId,
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        data: response.data || { error: true, message: 'Failed to add role.' },
      }),
    }),

    listRoles: build.query<ListRolesResponse, void>({
      query: () => ({ url: '/admin/list_roles', method: 'POST' }),
      providesTags: ['Roles'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || 'Role List',
        roles: response.roles || [],
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        data: response.data || { error: true, message: 'Failed to fetch roles.' },
      }),
    }),

    // ✅ Sends role_id as backend expects
    updateRole: build.mutation<UpdateRoleResponse, UpdateRoleRequest>({
      query: (data) => ({
        url: '/admin/update_role',
        method: 'POST',
        body: JSON.stringify({
          role_id: data.role_id,
          role_name: data.role_name.trim(),
          role_desc: data.role_desc.trim(),
        }),
      }),
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || 'Role updated successfully',
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        data: response.data || { error: true, message: 'Failed to update role.' },
      }),
    }),

    // ✅ Sends role_id as backend expects
    deleteRole: build.mutation<DeleteRoleResponse, number>({
      query: (role_id) => ({
        url: '/admin/delete_role',
        method: 'POST',
        body: JSON.stringify({ role_id }),
      }),
      invalidatesTags: ['Roles'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || 'Role deleted successfully',
      }),
      transformErrorResponse: (response: any) => ({
        status: response.status,
        data: response.data || { error: true, message: 'Failed to delete role.' },
      }),
    }),
  }),
});

export const {
  useAddRoleMutation,
  useListRolesQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = roleMasterApi;